import type { ReturnsExchangeOptionsResponsePromotionalExchange } from '@itsrever/rever-api'
import { calculateAmountDue } from './calculate-amount-due'

export type PromotionalExchange =
  ReturnsExchangeOptionsResponsePromotionalExchange

interface Replacement {
  productId?: string
  collectionIds?: string[]
}

// Mirrors IsInBxGyGetScope in returns-api, so the catalogue and the summary agree
export const keepsPromotion = (
  promotionalExchange: PromotionalExchange | undefined,
  { productId, collectionIds }: Replacement
): boolean => {
  if (!promotionalExchange) return false

  if (promotionalExchange.applies_to_all_products) return true

  // No product id keeps nothing, as in the Go predicate: it cannot be validated
  if (!productId) return false

  if (promotionalExchange.qualifying_product_ids?.includes(productId))
    return true

  return (collectionIds ?? []).some((collectionId) =>
    promotionalExchange.qualifying_collection_ids?.includes(collectionId)
  )
}

const toCents = (amount: number): number => Math.round(amount * 100)

// Mirrors BxGyCarriedUnitPrice in returns-api, in cents and with the same rounding:
// the promotion's discount off the replacement's own price, never below what the
// returned unit cost
export const carriedUnitPrice = (
  promotionalExchange: PromotionalExchange,
  unitPrice: number | null
): number => {
  const paidPrice = parseFloat(
    promotionalExchange.carried_unit_price?.amount ?? '0'
  )
  if (unitPrice === null) return paidPrice

  const unitCents = toCents(unitPrice)
  const paidCents = toCents(paidPrice)
  const percentage = promotionalExchange.carried_discount_percentage ?? 0
  const fixedAmount = promotionalExchange.carried_unit_discount?.amount
  // An API without either field priced every replacement at the paid price
  const discountCents =
    percentage > 0
      ? Math.round(unitCents * percentage)
      : fixedAmount !== undefined
        ? toCents(parseFloat(fixedAmount))
        : Infinity

  return (
    Math.min(unitCents, Math.max(unitCents - discountCents, paidCents)) / 100
  )
}

// The carried price is still owed, so leftover return credit offsets it like any price
export const promotionalAmountDue = (
  promotionalExchange: PromotionalExchange,
  unitPrice: number | null,
  remainingCredit: number
): number =>
  calculateAmountDue(
    carriedUnitPrice(promotionalExchange, unitPrice),
    remainingCredit
  )

export interface PromotionalCartLine {
  key: string
  returnedItemId: string
  quantity: number
  keepsPromotion: boolean
  // Units the returned item's promotion covers, shared by all of its lines
  promotionalUnits: number
}

export interface PromotionalAllocation {
  // Promotional units granted to each cart line, by line key
  unitsByLine: Map<string, number>
  // Promotional units still free for each returned item, by its line item id
  unusedUnits: Map<string, number>
}

// The summary consumes a promotion selection by selection: first line in, first served
export const allocatePromotionalUnits = (
  lines: PromotionalCartLine[]
): PromotionalAllocation => {
  const unusedUnits = new Map<string, number>()
  const unitsByLine = new Map<string, number>()

  lines.forEach((line) => {
    if (!unusedUnits.has(line.returnedItemId)) {
      unusedUnits.set(line.returnedItemId, line.promotionalUnits)
    }
    if (!line.keepsPromotion) {
      return
    }

    const unused = unusedUnits.get(line.returnedItemId) ?? 0
    const used = Math.min(unused, line.quantity)

    unitsByLine.set(line.key, used)
    unusedUnits.set(line.returnedItemId, unused - used)
  })

  return { unitsByLine, unusedUnits }
}

// The promotion covers `promotionalUnits`; any further unit pays its own price
export const promotionalLineTotal = (
  promotionalExchange: PromotionalExchange,
  unitPrice: number,
  quantity: number,
  promotionalUnits: number
): number => {
  const carried = Math.min(promotionalUnits, quantity)

  return (
    carried * carriedUnitPrice(promotionalExchange, unitPrice) +
    (quantity - carried) * unitPrice
  )
}
